import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatarDataHora } from "../../../utils/solicitacoes/formatters";
import { obterArquivoAnexo, preCarregarAnexos } from "../../../utils/solicitacoes/anexoCache";
import AnexoVisualizarModal, { ehVisualizavel } from "./AnexoVisualizarModal";
import AssinarAnexoModal from "./AssinarAnexoModal";
import { ehImagemAssinavel } from "../../../utils/solicitacoes/imagemParaPdf";

function ehPdf(nome = "") {
    return nome.split(".").pop()?.toLowerCase() === "pdf";
}

function formatarTamanho(bytes) {
    if (!bytes) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
}

function iconePorNome(nome = "") {
    const ext = nome.split(".").pop()?.toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "fa fa-file-image text-purple-500";
    if (["mp4", "webm", "ogg", "mov"].includes(ext)) return "fa fa-file-video text-blue-500";
    if (ext === "pdf") return "fa fa-file-pdf text-destructive";
    if (["xls", "xlsx", "csv"].includes(ext)) return "fa fa-file-excel text-success";
    if (["doc", "docx"].includes(ext)) return "fa fa-file-word text-primary";
    return "fa fa-file text-muted-foreground";
}

export default function AnexoList({ anexos = [], loading = false, onAssinar, onExcluir, usuarioLogadoId, etapaAtual, rateioItens, rateioItensDetalhados, formaPagamento }) {
    const [baixando, setBaixando] = useState(null);
    const [anexoVisualizando, setAnexoVisualizando] = useState(null);
    const [anexoAssinando, setAnexoAssinando] = useState(null);
    const [confirmandoExclusao, setConfirmandoExclusao] = useState(null);
    const [excluindo, setExcluindo] = useState(null);

    function excluir(anexo) {
        setExcluindo(anexo.id);
        onExcluir(anexo.id)
            .catch(error => {
                console.error("Erro ao remover anexo", error);
                toast.error("Erro ao remover anexo");
            })
            .finally(() => {
                setExcluindo(null);
                setConfirmandoExclusao(null);
            });
    }

    // Pré-carrega em segundo plano as imagens/PDFs da solicitação, para a primeira visualização já abrir pronta.
    useEffect(() => {
        preCarregarAnexos(anexos, anexo => ehVisualizavel(anexo.nomeOriginal));
    }, [anexos]);

    function baixar(anexo) {
        setBaixando(anexo.id);
        obterArquivoAnexo(anexo)
            .then(({ url }) => {
                const link = document.createElement("a");
                link.href = url;
                link.download = anexo.nomeOriginal;
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch(error => {
                console.error("Erro ao baixar anexo", error);
                toast.error("Erro ao baixar anexo");
            })
            .finally(() => setBaixando(null));
    }

    if (loading) {
        return (
            <div className="space-y-2 animate-pulse">
                {[1, 2].map(i => <div key={i} className="h-12 bg-muted rounded-lg" />)}
            </div>
        );
    }

    if (anexos.length === 0) {
        return <p className="text-sm text-muted-foreground text-center py-4">Nenhum anexo ainda.</p>;
    }

    return (
        <div className="flex flex-col gap-2">
            {anexos.map(anexo => (
                <div key={anexo.id} className="flex items-center gap-3 p-2.5 border border-border rounded-lg bg-card">
                    <i className={`${iconePorNome(anexo.nomeOriginal)} text-lg shrink-0`} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-medium text-foreground truncate" title={anexo.nomeOriginal}>{anexo.nomeOriginal}</p>
                            {anexo.descricao && (
                                <span className="shrink-0 inline-flex rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                                    {anexo.descricao}
                                </span>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            {formatarTamanho(anexo.tamanho)} · {anexo.usuarioNome} · {formatarDataHora(anexo.dataHora)}
                        </p>
                    </div>
                    {ehVisualizavel(anexo.nomeOriginal) && (
                        <button
                            type="button"
                            title="Visualizar anexo"
                            onClick={() => setAnexoVisualizando(anexo)}
                            className="shrink-0 p-2 rounded-full text-primary hover:bg-primary/10"
                        >
                            <i className="fa fa-eye" />
                        </button>
                    )}
                    {onAssinar && (ehPdf(anexo.nomeOriginal) || ehImagemAssinavel(anexo.nomeOriginal)) && (
                        <button
                            type="button"
                            title="Assinar documento"
                            onClick={() => setAnexoAssinando(anexo)}
                            className="shrink-0 p-2 rounded-full text-primary hover:bg-primary/10"
                        >
                            <i className="fa fa-signature" />
                        </button>
                    )}
                    <button
                        type="button"
                        title="Baixar anexo"
                        onClick={() => baixar(anexo)}
                        disabled={baixando === anexo.id}
                        className="shrink-0 p-2 rounded-full text-primary hover:bg-primary/10 disabled:opacity-50"
                    >
                        <i className={baixando === anexo.id ? "fa fa-spinner fa-spin" : "fa fa-download"} />
                    </button>
                    {onExcluir && anexo.usuarioId === usuarioLogadoId && anexo.etapa === etapaAtual && (
                        confirmandoExclusao === anexo.id ? (
                            <div className="shrink-0 flex items-center gap-1">
                                <button
                                    type="button"
                                    title="Confirmar remoção"
                                    onClick={() => excluir(anexo)}
                                    disabled={excluindo === anexo.id}
                                    className="p-2 rounded-full text-destructive hover:bg-destructive/10 disabled:opacity-50"
                                >
                                    <i className={excluindo === anexo.id ? "fa fa-spinner fa-spin" : "fa fa-check"} />
                                </button>
                                <button
                                    type="button"
                                    title="Cancelar"
                                    onClick={() => setConfirmandoExclusao(null)}
                                    disabled={excluindo === anexo.id}
                                    className="p-2 rounded-full text-muted-foreground hover:bg-muted disabled:opacity-50"
                                >
                                    <i className="fa fa-times" />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                title="Remover anexo"
                                onClick={() => setConfirmandoExclusao(anexo.id)}
                                className="shrink-0 p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                                <i className="fa fa-trash" />
                            </button>
                        )
                    )}
                </div>
            ))}

            <AnexoVisualizarModal anexo={anexoVisualizando} onClose={() => setAnexoVisualizando(null)} />
            {onAssinar && (
                <AssinarAnexoModal
                    anexo={anexoAssinando}
                    onClose={() => setAnexoAssinando(null)}
                    onAssinado={onAssinar}
                    rateioItens={rateioItens}
                    rateioItensDetalhados={rateioItensDetalhados}
                    formaPagamento={formaPagamento}
                />
            )}
        </div>
    );
}
